import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../shared/services/auth.service';
import { EmitterService } from '../../shared/services/emitter.service';
import { ApiService } from '../../shared/services/api.service';
import { ComponentStateService } from '../../shared/services/component-state.service';
import { AnalyticsService } from '../../shared/services/analytics.service';
import { AuthRedirectService } from '../../login/auth-redirect/auth-redirect.service';
import { OverlayService } from '../../shared/services/overlayService';
import { clearBrowserStorageKeepingPreferences } from '../../shared/utils/browser-storage.utils';
import {
  APP_TOKEN_STORAGE_KEY,
  IMPERSONATION_ORIGIN_ADMIN_CONSOLE,
  IMPERSONATION_ORIGIN_STORAGE_KEY,
} from '../../shared/constants';
import { Observable, tap } from 'rxjs';
import { currentAppToken } from '../../shared/auth/app-token';

@Injectable({
  providedIn: 'root',
})
export class AdminService {
  constructor(
    private readonly apiService: ApiService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly analyticsService: AnalyticsService,
    private readonly componentStateService: ComponentStateService,
    private readonly authRedirectService: AuthRedirectService,
    private readonly overlayService: OverlayService
  ) {}

  onBehalf(user) {
    return this.apiService.httpPost(`/api/admin/switchUser`, { username: user.username });
  }

  clearOriginalStorage() {
    this.componentStateService.clearIdleWatcher();
    clearBrowserStorageKeepingPreferences();
    EmitterService.get('user.logout').emit();
  }

  /**
   * Impersonation handed over by the platform admin console. Mirrors `switchUser()`, except the
   * console runs on another host and so cannot write this app's localStorage itself.
   *
   * The admin's own session here is stashed first, exactly as an in-app switch would: ending the
   * impersonation then restores it in place, without leaving this app. That session may not
   * exist — the admin signed in to the console, not necessarily to this app — in which case only
   * the origin marker is written and ending the impersonation falls back to a reload.
   *
   * Driven by {@link SwitchUserHandoffService} from the first app initializer.
   */
  switchUserFromConsole(token: string, email: string): Observable<void> {
    // Read before clearing: `isTokenExpired()` inspects what is in storage right now. A stale
    // token is worse than none — restoring it would bounce the admin through an auto-logout.
    const adminToken = currentAppToken();
    const adminUser = this.authService.getAuthUser();
    const canRestoreAdmin =
      !!adminToken && !this.authService.isTokenExpired() && !!(adminUser?.username ?? adminUser?.email);

    this.clearOriginalStorage();
    // Written synchronously, before the hydration below: the IAM SDK's session restore still has
    // the admin's IAM cookie and would otherwise mint the admin's app token and navigate as the
    // admin. With the markers already in place it sees `isImpersonating()` and takes
    // MtmAuthGlueService's impersonation branch instead.
    localStorage.setItem(APP_TOKEN_STORAGE_KEY, token);
    localStorage.setItem('onbehalf_user', JSON.stringify({ username: email, email }));
    localStorage.setItem(IMPERSONATION_ORIGIN_STORAGE_KEY, IMPERSONATION_ORIGIN_ADMIN_CONSOLE);
    if (canRestoreAdmin) {
      localStorage.setItem('admin_token', adminToken);
      localStorage.setItem('admin_user', JSON.stringify(adminUser));
    }

    const companyId = adminUser?.companyId || '';
    return this.authRedirectService.loginWithToken(token, email, '', { companyId }).pipe(
      tap(() => {
        // Replace the placeholder above with the user the gateway actually hydrated, so the
        // post-reload restore has the full record (see MtmAuthGlueService.onAppTokenReady).
        const onBehalfUser = this.authService.getAuthUser();
        if (onBehalfUser) {
          localStorage.setItem('onbehalf_user', JSON.stringify(onBehalfUser));
        }
      })
    );
  }

  switchToAdmin() {
    // Captured before the clears below wipe it.
    const fromConsole =
      localStorage.getItem(IMPERSONATION_ORIGIN_STORAGE_KEY) === IMPERSONATION_ORIGIN_ADMIN_CONSOLE;
    const adminToken = localStorage.getItem('admin_token');
    const adminUser = this.parseStoredUser(localStorage.getItem('admin_user'));
    const adminUsername = adminUser?.username ?? adminUser?.email;

    if (!adminToken || !adminUsername) {
      if (fromConsole) {
        // The admin had no session in this app to stash, so there is none to restore. End the
        // impersonation and reload: the IAM session cookie is still theirs, so the SDK signs them
        // back in as themselves — and lands on login if it cannot. Either way they stay here.
        this.clearOriginalStorage();
        this.navigateHard('/');
        return;
      }
      this.abortSwitchToAdmin();
      return;
    }

    this.clearOriginalStorage();
    const companyId = adminUser?.companyId || '';
    this.authRedirectService.loginWithToken(adminToken, adminUsername, adminUser.brandId, { companyId }).subscribe({
      // A console-initiated switch says nothing about admin rights in THIS app, so land on the
      // app root rather than its admin pages, which MtmAdminGuard may well refuse.
      next: () => this.navigateHard(fromConsole ? '/' : '/admin/users'),
      error: () => this.abortSwitchToAdmin(),
    });
  }

  private abortSwitchToAdmin(): void {
    this.clearOriginalStorage();
    this.overlayService.showError('Could not return to your admin account. Please sign in again.');
    this.navigateHard('/entry/login');
  }

  protected navigateHard(url: string): void {
    window.location.href = url;
  }

  private parseStoredUser(raw: string | null): { username?: string; email?: string; brandId?: string; companyId?: string } | null {
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  switchUser(credentials) {
    const adminToken = currentAppToken();
    const adminUser = this.authService.getAuthUser();
    this.clearOriginalStorage();
    this.authRedirectService.loginWithToken(credentials.auth.token, credentials.user.email, '', { companyId: credentials?.user?.companyId }).subscribe((res) => {
      localStorage.setItem('admin_token', adminToken);
      localStorage.setItem('admin_user', JSON.stringify(adminUser));
      // Preserve the impersonated user across the upcoming reload. The IAM SDK shares the
      // `auth_user` key and overwrites it with the admin on restore, so the glue restores this
      // user synchronously and re-hydrates the legacy session from the switch-user token (see
      // MtmAuthGlueService).
      const onBehalfUser = this.authService.getAuthUser();
      if (onBehalfUser) {
        localStorage.setItem('onbehalf_user', JSON.stringify(onBehalfUser));
      }
      this.router.navigateByUrl('/entry').then(() => {
        setTimeout(() => {
          window.location.reload();
        }, 0);
      });
    });
  }
}
